import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';

export const useAdminTours = (t, i18n) => {
    // --- State ---
    const [tours, setTours] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [cities, setCities] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [propertyIds, setPropertyIds] = useState([]);
    const [newTour, setNewTour] = useState({ type: 'zip', agency_id: '', file: null, tour_url: '', title: '', property_id: '', isNewPropertyId: false });
    const [editingTour, setEditingTour] = useState({});
    const [uploadProgress, setUploadProgress] = useState(0);
    const [tourIdFilter, setTourIdFilter] = useState('');
    const [agencyNameFilter, setAgencyNameFilter] = useState('');
    const [toast, setToast] = useState({ message: '', type: '', isVisible: false });

    const [isEditTourModalOpen, setIsEditTourModalOpen] = useState(false);
    const [isDeleteTourModalOpen, setIsDeleteTourModalOpen] = useState(false);
    const [tourToDelete, setTourToDelete] = useState(null);
    const [editTourSource, setEditTourSource] = useState('url'); // 'url' or 'zip'
    const [editTourFile, setEditTourFile] = useState(null);

    const fileInputRef = useRef();
    const editFileInputRef = useRef();

    // --- Fetch Functions ---
    const fetchAgencies = async () => {
        try {
            const res = await api.get('/agencies');
            setAgencies(res.data);
        } catch (err) {
            console.error('Failed to fetch agencies:', err);
        }
    };

    const fetchTours = useCallback(async () => {
        try {
            const res = await api.get('/tours');
            setTours(res.data);
        } catch (err) {
            console.error('Failed to fetch tours:', err);
        }
    }, []);

    const fetchCities = async () => {
        try {
            const res = await api.get('/locations/cities');
            setCities(res.data);
        } catch (err) {
            console.error('Failed to fetch cities:', err);
        }
    };

    const fetchPropertyTypes = async () => {
        try {
            const res = await api.get('/locations/property-types');
            setPropertyTypes(res.data);
        } catch (err) {
            console.error('Failed to fetch property types:', err);
        }
    };

    const fetchPropertyIds = async (agencyId = null) => {
        try {
            const params = {};
            if (agencyId) params.agency_id = agencyId;
            const res = await api.get('/tours/property-ids', { params });
            setPropertyIds(res.data);
        } catch (err) {
            console.error('Failed to fetch property IDs:', err);
        }
    };

    useEffect(() => {
        fetchAgencies();
        fetchTours();
        fetchCities();
        fetchPropertyTypes();
    }, [fetchTours]);

    // Auto-refresh tours every X seconds to update view counts
    useEffect(() => {
        const refreshInterval = parseInt(import.meta.env.VITE_AUTO_REFRESH_INTERVAL) || 60000;
        const interval = setInterval(() => {
            fetchTours();
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [fetchTours]);

    // Fetch property IDs when agency is selected
    useEffect(() => {
        if (newTour.agency_id) {
            fetchPropertyIds(newTour.agency_id);
        }
    }, [newTour.agency_id]);

    // --- Handlers ---
    const handleEditTourClick = (tour) => {
        setEditingTour(tour);
        // Determine source based on existing tour data
        if (tour.tour_url) {
            setEditTourSource('url');
        } else if (tour.file_path) {
            setEditTourSource('zip');
        } else {
            setEditTourSource('url');
        }
        setEditTourFile(null);
        setIsEditTourModalOpen(true);
    };

    const handleDeleteTour = (tourId) => {
        setTourToDelete(tourId);
        setIsDeleteTourModalOpen(true);
    };

    // --- Submit Handlers ---
    const handleNewTourSubmit = async (e) => {
        e.preventDefault();

        if (!newTour.agency_id) {
            alert(t('selectAgency'));
            return;
        }

        if (newTour.type === 'zip' && !newTour.file) {
            alert(t('selectFile'));
            return;
        }
        if (newTour.type === 'url' && !newTour.tour_url) {
            alert(t('enterTourUrl') || "Please enter a tour URL");
            return;
        }

        const formData = new FormData();
        formData.append('title', newTour.title);
        formData.append('property_id', newTour.property_id);
        formData.append('agency_id', newTour.agency_id);

        if (newTour.type === 'zip' && newTour.file) {
            formData.append('file', newTour.file);
        } else if (newTour.type === 'url' && newTour.tour_url) {
            formData.append('tour_url', newTour.tour_url);
        }

        try {
            setUploadProgress(0);
            await api.post('/tours/upload', formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });
            setNewTour({
                title: '', property_id: '', agency_id: '', file: null, tour_url: '', type: 'zip', isNewPropertyId: false
            });
            // Clear the file input manually
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setUploadProgress(0);
            fetchTours();
            setToast({
                isVisible: true,
                message: t('tourUploadedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            setUploadProgress(0);
            setToast({
                isVisible: true,
                message: t('uploadFailed'),
                type: 'error'
            });
        }
    };

    const handleEditTourSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();

            // Add basic tour data
            formData.append('title', editingTour.title || '');
            formData.append('description', editingTour.description || '');
            formData.append('property_id', editingTour.property_id || '');
            formData.append('tour_source', editTourSource);

            // Add source-specific data
            if (editTourSource === 'url') {
                formData.append('tour_url', editingTour.tour_url || '');
            } else if (editTourSource === 'zip' && editTourFile) {
                formData.append('file', editTourFile);
            }

            setUploadProgress(0);
            await api.put(`/tours/${editingTour.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            setUploadProgress(0);
            setIsEditTourModalOpen(false);
            setEditTourFile(null);
            fetchTours();
            setToast({
                isVisible: true,
                message: t('updateSuccess'),
                type: 'success'
            });
        } catch (err) {
            console.error('Failed to update tour:', err);
            setUploadProgress(0);
            setToast({
                isVisible: true,
                message: t('errorUpdatingTour'),
                type: 'error'
            });
        }
    };

    const confirmDeleteTour = async () => {
        if (!tourToDelete) return;

        try {
            await api.delete(`/tours/${tourToDelete}`);
            setIsDeleteTourModalOpen(false);
            setTourToDelete(null);
            fetchTours();

            setToast({
                isVisible: true,
                message: t('tourDeletedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            setIsDeleteTourModalOpen(false);
            setTourToDelete(null);

            setToast({
                isVisible: true,
                message: t('tourDeleteFailed'),
                type: 'error'
            });
        }
    };

    // Filter tours
    const filteredTours = tours.filter(tour => {
        const agencyName = i18n.language === 'ar'
            ? (agencies.find(a => a.id === tour.agency_id)?.name_ar || agencies.find(a => a.id === tour.agency_id)?.name)
            : (agencies.find(a => a.id === tour.agency_id)?.name_en || agencies.find(a => a.id === tour.agency_id)?.name);

        const matchesTourId = tour.property_id.toLowerCase().includes(tourIdFilter.toLowerCase());
        const matchesAgency = agencyName?.toLowerCase().includes(agencyNameFilter.toLowerCase()) || !agencyNameFilter;

        return matchesTourId && matchesAgency;
    });

    return {
        tours,
        agencies,
        cities,
        propertyTypes,
        propertyIds,
        newTour,
        setNewTour,
        editingTour,
        setEditingTour,
        uploadProgress,
        tourIdFilter,
        setTourIdFilter,
        agencyNameFilter,
        setAgencyNameFilter,
        toast,
        setToast,
        isEditTourModalOpen,
        setIsEditTourModalOpen,
        isDeleteTourModalOpen,
        setIsDeleteTourModalOpen,
        tourToDelete,
        setTourToDelete,
        editTourSource,
        setEditTourSource,
        editTourFile,
        setEditTourFile,
        fileInputRef,
        editFileInputRef,
        handleEditTourClick,
        handleDeleteTour,
        handleNewTourSubmit,
        handleEditTourSubmit,
        confirmDeleteTour,
        filteredTours
    };
};
